import { useEffect } from "react";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { BRAND } from "@/lib/brand";
import { useCanonical } from "@/lib/useCanonical";

const Security = () => {
  useCanonical({ path: "/security" });

  useEffect(() => {
    document.title = "Security & Privacy | Myelina Health";
    const meta = document.querySelector('meta[name="description"]');
    meta?.setAttribute(
      "content",
      "How Myelina Health protects your health and energy data: encryption, row-level security, account deletion, and what we never do with your data."
    );
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-6 sm:px-10 lg:px-16 py-24 max-w-3xl">
        <h1 className="font-display text-5xl md:text-6xl tracking-tight mb-6">Security &amp; privacy</h1>
        <p className="lead-italic text-foreground/80 mb-12">
          Your energy data is yours. Here's exactly how we protect it.
        </p>

        <section className="space-y-8 text-[16px] leading-[1.8] text-foreground/85">
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Encryption</h2>
            <p>All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Database backups are encrypted and stored in isolated regions.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Row-level security, in plain English</h2>
            <p>Every row in our database carries a user ID. Database rules, enforced at the database level, not just in app code, make it impossible for one account to read another account's check-ins, reflections, medications, or AI conversations. Even our admin tools cannot bypass this for individual records without explicit, audited intent.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Account deletion</h2>
            <p>Deleting your account purges every related row: profile, daily check-ins, evening reflections, medications, wearable imports, AI conversations, buddy links, email logs, and subscription records. This is irreversible and happens within minutes, not days.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What we never do</h2>
            <ul className="list-disc pl-6 space-y-2">
              <li>Sell your data. Ever. To anyone.</li>
              <li>Share identifiable data with insurers, employers, or advertisers.</li>
              <li>Use your check-ins to train public AI models.</li>
              <li>Track you across other sites with our cookies.</li>
            </ul>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">What Myelina sees</h2>
            <p>Our AI agent (Myelina) processes your data through privacy-respecting model providers under strict contracts. Prompts and responses are not retained for training. We only send the minimum context required to give you a useful pattern observation.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Compliance posture</h2>
            <p>{BRAND.name} is built to align with GDPR (EU/UK) and CCPA (California). We are not a HIPAA-covered entity, we are a wellness tool, not a medical device, and our architecture follows privacy-first data-minimization and access-control principles.</p>
          </div>
          <div>
            <h2 className="font-display text-2xl mb-3 text-foreground">Reporting a vulnerability</h2>
            <p>Email security@myelinahealth.com with details. We respond within 48 hours and credit responsible disclosure in our changelog.</p>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Security;
